// src/components/MarketContext.tsx: 2x2 grid of market indicator cards with
// sparklines over a selectable 1Y / 3Y / 5Y window, shown at the top of the
// dashboard for every role.
import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine,
} from 'recharts';
import {
  fetchMarketContext, indicatorColour, MarketIndicator,
  MarketPeriod, MARKET_PERIODS,
} from '../lib/marketData';

const C = {
  navy: '#0A1628', gold: '#C9A84C', white: '#FFFFFF',
  surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A',
};

const SHIMMER_CSS = `
@keyframes altvenoShimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.altveno-skeleton {
  background: linear-gradient(90deg, #F5F6F8 25%, #E8EBF0 50%, #F5F6F8 75%);
  background-size: 200% 100%;
  animation: altvenoShimmer 1.5s infinite linear;
  border-radius: 3px;
}`;

const cardStyle: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${C.borderLt}`,
  borderRadius: '4px',
  padding: '16px',
};

function SparkTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const point = payload[0];
  // Read the date off the row rather than from `label`. Without an XAxis
  // dataKey recharts passes the array index as the label, and new Date(0)
  // renders every point as 1 January 1970.
  const date = point?.payload?.date;
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
      padding: '6px 10px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
      fontFamily: 'system-ui', fontSize: '11px',
    }}>
      <div style={{ color: C.muted, marginBottom: '2px' }}>
        {date
          ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : '--'}
      </div>
      <div style={{ color: C.text, fontWeight: 700, fontFamily: '"Courier New", monospace' }}>
        {Number(point.value).toLocaleString('en-US', { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

function IndicatorCard({ ind }: { ind: MarketIndicator }) {
  const colour = indicatorColour(ind, C);
  const gradientId = `spark-${ind.label.replace(/[^a-zA-Z0-9]/g, '')}`;
  const hasHistory = ind.history.length > 1;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', marginBottom: '10px' }}>
        <div>
          <div style={{
            fontSize: '9px', fontWeight: 700, color: C.muted,
            letterSpacing: '1.2px', textTransform: 'uppercase',
            fontFamily: 'system-ui', marginBottom: '4px',
          }}>{ind.label}</div>
          <div style={{
            fontSize: '22px', fontWeight: 600, color: C.navy,
            fontFamily: '"Courier New", monospace', letterSpacing: '-0.5px', lineHeight: 1,
          }}>{ind.value}</div>
        </div>
        {ind.yoyLabel && (
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '13px', fontWeight: 700, color: colour,
              fontFamily: '"Courier New", monospace', lineHeight: 1,
            }}>{ind.yoyLabel}</div>
            {/* The span the series actually covers, not the requested period.
                FRED retains only ~3 years of the HY spread, so on a 5Y view
                that card says "3Y" while the others say "5Y". */}
            <div style={{ fontSize: '9px', color: C.mutedLt, marginTop: '3px', fontFamily: 'system-ui' }}>
              {ind.spanLabel || ''}
            </div>
          </div>
        )}
      </div>

      <div style={{ height: '60px', marginLeft: '-4px', marginRight: '-4px' }}>
        {hasHistory ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ind.history} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colour} stopOpacity={0.1} />
                  <stop offset="100%" stopColor={colour} stopOpacity={0} />
                </linearGradient>
              </defs>
              {/* Hidden axes: the sparkline uses the full card height with no
                  labels. The X axis still needs the date key so the tooltip
                  and cursor track real dates rather than array indices. */}
              <XAxis dataKey="date" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip content={<SparkTooltip />} cursor={{ stroke: C.border, strokeWidth: 1 }} />
              {ind.startValue !== null && (
                <ReferenceLine y={ind.startValue} stroke={C.border} strokeDasharray="2 3" strokeWidth={1} />
              )}
              <Area
                type="monotone" dataKey="value"
                stroke={colour} strokeWidth={1.5}
                fill={`url(#${gradientId})`}
                dot={false} activeDot={{ r: 2.5, fill: colour, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', color: C.mutedLt, fontFamily: 'system-ui',
          }}>No history available</div>
        )}
      </div>

      <div style={{ fontSize: '10px', color: C.mutedLt, marginTop: '8px', fontFamily: 'system-ui' }}>
        {ind.description}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={cardStyle}>
      <div className="altveno-skeleton" style={{ width: '52px', height: '9px', marginBottom: '10px' }} />
      <div className="altveno-skeleton" style={{ width: '92px', height: '22px', marginBottom: '12px' }} />
      <div className="altveno-skeleton" style={{ height: '60px' }} />
      <div className="altveno-skeleton" style={{ width: '120px', height: '10px', marginTop: '10px' }} />
    </div>
  );
}

export default function MarketContext() {
  const [indicators, setIndicators] = useState<MarketIndicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [period, setPeriod] = useState<MarketPeriod>('1Y');

  useEffect(() => {
    let cancelled = false;
    // Skeletons while a longer window loads, rather than leaving the previous
    // period's sparklines on screen under the new label.
    setLoading(true);

    async function load() {
      try {
        const data = await fetchMarketContext(period);
        if (cancelled) return;
        setIndicators(data);
        // Only stamp a time when real data came back, so the header never
        // claims freshness while the cards show dashes.
        if (data.some(d => d.source)) {
          setLastUpdated(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
        }
      } catch (e) {
        console.warn('Market context failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [period]);

  return (
    <div style={{ marginBottom: '24px' }}>
      <style>{SHIMMER_CSS}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '10px' }}>
        <div style={{
          fontSize: '10px', fontWeight: 700, color: C.gold,
          letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'system-ui',
        }}>Market Context</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {lastUpdated && (
            <div style={{ fontSize: '9px', color: C.mutedLt, fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
              Updated {lastUpdated}
            </div>
          )}
          <div style={{ display: 'flex', border: `1px solid ${C.borderLt}`, borderRadius: '3px', overflow: 'hidden' }}>
            {MARKET_PERIODS.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                style={{
                  padding: '4px 10px',
                  border: 'none',
                  borderLeft: p === MARKET_PERIODS[0] ? 'none' : `1px solid ${C.borderLt}`,
                  background: period === p ? C.navy : C.white,
                  color: period === p ? C.white : C.muted,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  fontFamily: 'system-ui',
                  cursor: 'pointer',
                }}
              >{p}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
        {loading
          ? [0, 1, 2, 3].map(i => <SkeletonCard key={i} />)
          : indicators.map(ind => <IndicatorCard key={ind.label} ind={ind} />)}
      </div>
    </div>
  );
}
